import { type HighlightedCell, useActiveGridHighlight } from "@/grid/GridCellHighlightProvider";
import { useFormWithUtils } from "@/overlay/components/SmartForm/SmartFormUtils/FormHooks/useFormUtils";
import type { BaseFormProps } from "@/overlay/components/SmartForm/SmartFormUtils/commonTypeUtils";
import { useAppDispatch } from "@/reducer";
import { discardNote, updateNote, useNote } from "@/reducer/NoteState";
import type { NoteCreate, NoteUpdate } from "@mm/metx-workbench.meteomatics.com";
import type { SubmitHandler, UseFormReturn } from "react-hook-form";

export type NotePatch = Partial<NoteCreate | NoteUpdate>;
/**
 * Form data passed from the child components.
 */
export type NoteFormData = {
  title: string;
};

export type NoteFormProps = {
  defaultFormData: NotePatch;
} & BaseFormProps<NoteFormData> & {
    onRemove: () => void;
    formMethods: UseFormReturn<NoteFormData>;
    setHighlighted: (cell: HighlightedCell | undefined) => void;
  };

export function useNoteForm(toolId: number): NoteFormProps {
  const note = useNote(toolId);
  const { setHighlighted } = useActiveGridHighlight();
  const { formMethods } = useFormWithUtils<NoteFormData>({
    mode: "onChange",
    defaultValues: { ...note, title: note.title || undefined },
  });

  const dispatch = useAppDispatch();

  const onSubmit: SubmitHandler<NoteFormData> = (formData) => {
    dispatch(updateNote({ id: note.id, note: formData }));
  };

  const onRemove = () => {
    dispatch(discardNote({ id: toolId }));
  };

  return {
    defaultFormData: note,
    submitMode: "onChange",
    onSubmit,
    onRemove,
    setHighlighted: setHighlighted,
    formMethods,
  };
}
